import {useMutation} from '@apollo/react-hooks';

import type {
    DeleteEmailSequenceTemplate as DeleteEmailSequenceTemplateMutation,
    DeleteEmailSequenceTemplateVariables as DeleteEmailSequenceTemplateMutationVariables,
    GetEmailTemplateGroup,
    GetUncategorizedEmailSequenceTemplates,
} from 'nutshell-graphql-types';

import GET_EMAIL_TEMPLATE_GROUP from '../../templates/template-groups/graphql/queries/get-email-template-group.graphql';
import GET_UNCATEGORIZED_EMAIL_SEQUENCE_TEMPLATES from '../../templates/template-groups/graphql/queries/get-uncategorized-email-sequence-templates.graphql';

import DELETE_EMAIL_SEQUENCE from '../mutations/delete-email-sequence-template.graphql';

export function useDeleteEmailSequenceTemplate(): {
    deleteEmailSequenceTemplate: (id: string, queryVariables: any) => Promise<any>;
} {
    const [deleteEmailSequenceTemplate] = useMutation<
        DeleteEmailSequenceTemplateMutation,
        DeleteEmailSequenceTemplateMutationVariables
    >(DELETE_EMAIL_SEQUENCE);

    return {
        deleteEmailSequenceTemplate: (id: string, queryVariables: any) =>
            deleteEmailSequenceTemplate({
                variables: {
                    sequenceTemplate: {id},
                },
                update: (proxy, {data}) => {
                    const deletedTemplate =
                        data &&
                        data.emailSequenceTemplateDelete &&
                        data.emailSequenceTemplateDelete.emailSequenceTemplate;

                    if (!deletedTemplate) return;

                    if (deletedTemplate.group) {
                        const groupCacheResult: GetEmailTemplateGroup | null | undefined =
                            proxy.readQuery({
                                query: GET_EMAIL_TEMPLATE_GROUP,
                                variables: {id: deletedTemplate.group.id, ...queryVariables},
                            });

                        if (groupCacheResult && deletedTemplate.group) {
                            proxy.writeQuery({
                                query: GET_EMAIL_TEMPLATE_GROUP,
                                variables: {id: deletedTemplate.group.id, ...queryVariables},
                                data: {
                                    emailSequenceTemplateGroup: {
                                        ...groupCacheResult.emailSequenceTemplateGroup,
                                        emailSequenceTemplates:
                                            groupCacheResult.emailSequenceTemplateGroup.emailSequenceTemplates.filter(
                                                (template) => template.id !== deletedTemplate.id
                                            ),
                                    },
                                },
                            });
                        }
                    } else {
                        const uncategorizedCacheResult:
                            | GetUncategorizedEmailSequenceTemplates
                            | null
                            | undefined = proxy.readQuery({
                            query: GET_UNCATEGORIZED_EMAIL_SEQUENCE_TEMPLATES,
                            variables: {...queryVariables},
                        });

                        if (uncategorizedCacheResult) {
                            proxy.writeQuery({
                                query: GET_UNCATEGORIZED_EMAIL_SEQUENCE_TEMPLATES,
                                variables: {...queryVariables},
                                data: {
                                    emailSequenceTemplateGroups: {
                                        ...uncategorizedCacheResult.emailSequenceTemplateGroups,
                                        uncategorized:
                                            uncategorizedCacheResult.emailSequenceTemplateGroups.uncategorized.filter(
                                                (template) => template.id !== deletedTemplate.id
                                            ),
                                    },
                                },
                            });
                        }
                    }
                },
            }),
    };
}
